import { getStakeContract, getReadWeb3 } from "../index";
import { ZERO_ADDRESS, getCardLevelByTokenId } from "../constants";
import { DEFAULT_PARENT_ADDRESS, CARD_USER_PROFILE } from "@/views/pledge/config";
import {
  ensureUsdtAllowanceForStake,
} from "./tokenService";
import {
  assertWalletReady,
  formatTokenAmount,
  parseTokenAmount,
  sendContractMethod,
} from "./txHelper";

function logClaimTransactionHash(label, receipt) {
  const hash = receipt?.transactionHash;
  console.log(`[${label}] transactionHash:`, hash);
  return hash;
}

function formatDate(timestamp) {
  const date = new Date(Number(timestamp) * 1000);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

async function fetchStakeParams() {
  const stake = getStakeContract();
  const [minDepositAmount, daySeconds, dailyRate180, dailyRate360] =
    await Promise.all([
      stake.methods.minDepositAmount().call(),
      stake.methods.daySeconds().call(),
      stake.methods.dailyRate180().call(),
      stake.methods.dailyRate360().call(),
    ]);
  return {
    minDepositAmount,
    daySeconds: Number(daySeconds),
    dailyRate180: Number(dailyRate180),
    dailyRate360: Number(dailyRate360),
  };
}

async function calcPendingRewardRusd(stakeInfo, params) {
  const now = Math.floor(Date.now() / 1000);
  const startTime = Number(stakeInfo.startTime);
  const lockDays = Number(stakeInfo.lockDays);
  const lastRewardTime = Number(stakeInfo.lastRewardTime);
  const stakeAmount = BigInt(stakeInfo.stakeAmount);

  const maturityTime = startTime + lockDays * params.daySeconds;
  if (lastRewardTime >= maturityTime) {
    return 0n;
  }

  const endTime = now > maturityTime ? maturityTime : now;
  const pendingTime = endTime - lastRewardTime;
  if (pendingTime <= 0) {
    return 0n;
  }

  const rate =
    lockDays === 180 ? params.dailyRate180 : params.dailyRate360;
  const pendingU =
    (stakeAmount * BigInt(rate) * BigInt(pendingTime)) /
    (BigInt(params.daySeconds) * 10000n);

  if (pendingU <= 0n) {
    return 0n;
  }

  const stake = getStakeContract();
  const rusdAmount = await stake.methods.getRusdRewardAmount(String(pendingU)).call();
  return BigInt(rusdAmount);
}

export async function getMinDepositAmount() {
  const stake = getStakeContract();
  const min = await stake.methods.minDepositAmount().call();
  return formatTokenAmount(min);
}

export async function getParentAddress(userAddress) {
  const stake = getStakeContract();
  const parent = await stake.methods.getParent(userAddress).call();
  if (!parent || parent === ZERO_ADDRESS) {
    return "";
  }
  return parent;
}

function countUserCards(cardIds) {
  let goldCards = 0;
  let silverCards = 0;
  let bronzeCards = 0;

  cardIds.forEach((tokenId) => {
    const level = getCardLevelByTokenId(tokenId);
    if (level === "gold") goldCards += 1;
    if (level === "silver") silverCards += 1;
    if (level === "bronze") bronzeCards += 1;
  });

  return { goldCards, silverCards, bronzeCards };
}

function resolveCardUserProfile(counts) {
  const { goldCards, silverCards, bronzeCards } = counts;
  if (goldCards + silverCards + bronzeCards === 0) {
    return null;
  }
  if (goldCards > 0) {
    return { level: "gold", ...CARD_USER_PROFILE.gold, ...counts };
  }
  if (silverCards > 0) {
    return { level: "silver", ...CARD_USER_PROFILE.silver, ...counts };
  }
  return { level: "bronze", ...CARD_USER_PROFILE.bronze, ...counts };
}

export async function fetchUserCardProfile(userAddress) {
  if (!userAddress) {
    return null;
  }
  const stake = getStakeContract();
  const cardIds = await stake.methods.getUserCards(userAddress).call();
  return resolveCardUserProfile(countUserCards(cardIds));
}

export async function fetchStakingRecords(userAddress) {
  const stake = getStakeContract();
  const params = await fetchStakeParams();
  const stakes = await stake.methods.getUserStakes(userAddress).call();
  const now = Math.floor(Date.now() / 1000);

  const records = await Promise.all(
    stakes.map(async (item, index) => {
      const startTime = Number(item.startTime);
      const lockDays = Number(item.lockDays);
      const maturityTime = startTime + lockDays * params.daySeconds;
      const pendingRusd = await calcPendingRewardRusd(item, params);

      return {
        id: index + 1,
        index,
        time: formatDate(item.startTime),
        type: "质押",
        amount: formatTokenAmount(item.stakeAmount),
        lpAmount: formatTokenAmount(item.lpAmount),
        reward: formatTokenAmount(String(pendingRusd)),
        canRedeem: now >= maturityTime && !item.isPrincipalClaimed,
        hasPendingReward: pendingRusd > 0n,
      };
    })
  );

  return records;
}

export async function fetchPersonalInfo(userAddress) {
  const stake = getStakeContract();
  const [
    userInfo,
    claimedReward,
    cardIds,
  ] = await Promise.all([
    stake.methods.getUserInfo(userAddress).call(),
    stake.methods.getClaimedReward(userAddress).call(),
    stake.methods.getUserCards(userAddress).call(),
  ]);

  let goldCards = 0;
  let silverCards = 0;
  let bronzeCards = 0;
  const counts = countUserCards(cardIds);
  goldCards = counts.goldCards;
  silverCards = counts.silverCards;
  bronzeCards = counts.bronzeCards;

  return {
    goldCards,
    silverCards,
    bronzeCards,
    cardReward: `${formatTokenAmount(userInfo[0])} RUSD`,
    directReferrals: Number(userInfo[1]),
    directPerformance: `${formatTokenAmount(userInfo[2])} USDT`,
    totalPerformance: `${formatTokenAmount(userInfo[3])} USDT`,
    promotionReward: `${formatTokenAmount(userInfo[4])} RUSD`,
    withdrawnStaking: `${formatTokenAmount(claimedReward[0])} RUSD`,
    withdrawnPromotion: `${formatTokenAmount(claimedReward[1])} RUSD`,
    withdrawnCard: `${formatTokenAmount(claimedReward[2])} RUSD`,
    pendingPromotionReward: BigInt(userInfo[4]),
    pendingCardReward: BigInt(userInfo[0]),
  };
}

export async function stakeToken(amount, lockDays, parentAddress) {
  assertWalletReady();
  const amountWei = parseTokenAmount(amount);
  await ensureUsdtAllowanceForStake(amountWei);

  const stake = getStakeContract();
  const parent = parentAddress || ZERO_ADDRESS;
  const method = stake.methods.stakeToken(amountWei, lockDays, parent);
  return sendContractMethod(method);
}

export async function claimPrincipal(index) {
  assertWalletReady();
  const stake = getStakeContract();
  const method = stake.methods.claimPrincipal(index);
  return sendContractMethod(method);
}

export async function claimAllStakeRewards(userAddress) {
  assertWalletReady();
  const records = await fetchStakingRecords(userAddress);
  const pendingRecords = records.filter((item) => item.hasPendingReward);
  if (pendingRecords.length === 0) {
    throw new Error("暂无可提取的质押收益");
  }

  const stake = getStakeContract();
  for (const record of pendingRecords) {
    const method = stake.methods.claimStakeReward(record.index);
    const receipt = await sendContractMethod(method);
    console.log(receipt);
    logClaimTransactionHash("提取质押收益", receipt);
  }
}

export async function claimShareReward() {
  assertWalletReady();
  const stake = getStakeContract();
  const method = stake.methods.claimShareReward();
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("提取推广权益", receipt);
  return receipt;
}

export async function claimCardsReward() {
  assertWalletReady();
  const stake = getStakeContract();
  const method = stake.methods.claimCardsReward();
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("提取卡牌权益", receipt);
  return receipt;
}

export function getParentFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const parent = params.get("parent");
  if (!parent || !getReadWeb3().utils.isAddress(parent)) {
    return "";
  }
  return getReadWeb3().utils.toChecksumAddress(parent);
}

/** @deprecated 使用 getParentFromUrl */
export function getInviteParentFromUrl() {
  return getParentFromUrl();
}

export function resolveStakeParentAddress(boundParent) {
  if (boundParent && boundParent !== ZERO_ADDRESS) {
    return boundParent;
  }
  return getParentFromUrl() || DEFAULT_PARENT_ADDRESS;
}
